import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import { SERVER_ORIGIN } from '../../utilities/constants';

const YuvaEcAssignmentModal = ({ show, onHide, student, onSuccess }) => {
    const [formData, setFormData] = useState({
        effectiveFrom: '',
        effectiveTo: '',
        position: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [existingEcStatus, setExistingEcStatus] = useState(null);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [roles, setRoles] = useState([]);

    const toDateInputValue = (value) => {
        if (!value) return "";
        try {
            const d = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(d.getTime())) return "";
            return d.toISOString().split("T")[0];
        } catch {
            return "";
        }
    };

    const resolvePrefillFromExistingStatus = (status, currentStudent) => {
        const current = status?.currentEC;
        if (!current) return null;

        const studentId = currentStudent?.userId || currentStudent?._id;
        const currentId = current?.userId || current?._id;
        const samePerson =
            (studentId && currentId && String(studentId) === String(currentId)) ||
            (currentStudent?.email && current?.email && currentStudent.email === current.email);

        if (!samePerson) return null;

        return {
            effectiveFrom: toDateInputValue(current?.yuvaEcEffectiveFrom),
            effectiveTo: toDateInputValue(current?.yuvaEcEffectiveTo),
            position: current?.yuvaEcPosition || "",
        };
    };

    // Check existing YUVA EC status when modal opens
    useEffect(() => {
        if (show && student?.collegeName) {
            checkExistingEcStatus();
        }
    }, [show, student]);

    // Load YUVA EC roles when modal opens


    const fetchRoles = async () => {
        try {
            const response = await fetch(`${SERVER_ORIGIN}/api/admin/auth/yuva-ec-roles/all`, {
                headers: { 'auth-token': sessionStorage.getItem('token') }
            });

            console.log('Fetching YUVA EC roles, response status:', response);
            const result = await response.json();
            console.log('YUVA EC roles fetched:', result);
            if (response.ok) {
                const list = result.data || [];
                console.log('YUVA EC roles list:', list);
                setRoles(list);
                // Default only when user hasn't got any EC position yet
                setFormData((prev) => {
                    const alreadyHasPosition = Boolean(
                        prev.position ||
                        student?.yuvaEcPosition ||
                        student?.yuvaEcRole ||
                        student?.position
                    );
                    if (alreadyHasPosition || list.length === 0) return prev;
                    const firstActive = list.find((r) => r.isActive && !r.isDeleted) || list[0];
                    return { ...prev, position: firstActive?.name || prev.position };
                });
            }
        } catch (error) {
            console.error('Error fetching YUVA EC roles:', error);
        }
    };
    useEffect(() => {
        if (show) {
            fetchRoles();
        }
    }, [show]);

    const checkExistingEcStatus = async () => {
        setIsCheckingStatus(true);
        try {
            const response = await fetch(
                `${SERVER_ORIGIN}/api/admin/auth/yuva-ec-status?institute=${encodeURIComponent(student.collegeName)}`,
                {
                    headers: {
                        'auth-token': sessionStorage.getItem('token'),
                    }
                }
            );

            if (response.ok) {
                const result = await response.json();
                setExistingEcStatus(result.data);
            }
        } catch (error) {
            console.error('Error checking YUVA EC status:', error);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    // const handleInputChange = (e) => {
    //     const { name, value } = e.target;
    //     setFormData(prev => ({
    //         ...prev,
    //         [name]: value
    //     }));
    // };
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "effectiveFrom") {
            const fromDate = new Date(value);
            const toDate = new Date(fromDate);
            toDate.setFullYear(fromDate.getFullYear() + 1);

            setFormData(prev => ({
                ...prev,
                effectiveFrom: value,
                effectiveTo: toDate.toISOString().split("T")[0]
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // useEffect(() => {
    //     if (show) {
    //         const today = new Date();
    //         const nextYear = new Date(today);
    //         nextYear.setFullYear(today.getFullYear() + 1);

    //         setFormData(prev => ({
    //             ...prev,
    //             effectiveFrom: today.toISOString().split("T")[0],
    //             effectiveTo: nextYear.toISOString().split("T")[0]
    //         }));
    //     }
    // }, [show]);
    useEffect(() => {
        console.log('YUVA EC Assignment Modal opened. Student:', student);
        if (!show) return;

        const fromStudent = {
            effectiveFrom: toDateInputValue(student?.yuvaEcEffectiveFrom),
            effectiveTo: toDateInputValue(student?.yuvaEcEffectiveTo),
            position: student?.yuvaEcPosition || student?.yuvaEcRole || student?.position || "",
        };

        const fromStatus = resolvePrefillFromExistingStatus(existingEcStatus, student);

        const hasStudentData =
            Boolean(fromStudent.effectiveFrom) ||
            Boolean(fromStudent.effectiveTo) ||
            Boolean(fromStudent.position);

        if (hasStudentData) {
            setFormData({
                effectiveFrom: fromStudent.effectiveFrom || "",
                effectiveTo: fromStudent.effectiveTo || "",
                position: fromStudent.position || "",
            });
            return;
        }

        if (fromStatus) {
            setFormData({
                effectiveFrom: fromStatus.effectiveFrom || "",
                effectiveTo: fromStatus.effectiveTo || "",
                position: fromStatus.position || "",
            });
            return;
        }

        const today = new Date();
        const nextYear = new Date(today);
        nextYear.setFullYear(today.getFullYear() + 1);
        setFormData((prev) => ({
            ...prev,
            effectiveFrom: prev.effectiveFrom || today.toISOString().split("T")[0],
            effectiveTo: prev.effectiveTo || nextYear.toISOString().split("T")[0],
        }));
    }, [show, student, existingEcStatus]);

    const roleOptions = useMemo(() => {
        const selected = formData.position;
        const base = Array.isArray(roles) ? roles : [];
        // Important: If current assigned role is inactive, still show it so the select can display the value.
        return base.filter((r) => {
            if (r?.isDeleted) return false;
            if (r?.isActive) return true;
            return selected && r?.name === selected;
        });
    }, [roles, formData.position]);


    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.position) {
            toast.error('Please select a Role');
            return;
        }

        if (!formData.effectiveFrom || !formData.effectiveTo) {
            toast.error('Please select both effective from and to dates');
            return;
        }

        if (new Date(formData.effectiveFrom) >= new Date(formData.effectiveTo)) {
            toast.error('Effective from date must be before effective to date');
            return;
        }

        setIsLoading(true);

        try {
            const requestData = {
                studentId: student?.userId || student?._id,
                effectiveFrom: formData.effectiveFrom,
                effectiveTo: formData.effectiveTo,
                position: formData.position
            };

            console.log('Sending YUVA EC assignment request:', requestData);
            console.log('Student object:', student);

            const response = await fetch(`${SERVER_ORIGIN}/api/admin/auth/assign-yuva-ec`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': sessionStorage.getItem('token'),
                },
                body: JSON.stringify(requestData),
            });

            const result = await response.json();

            console.log('YUVA EC assignment response:', result);

            if (response.ok) {
                toast.success('YUVA EC assigned successfully!');
                setFormData({ effectiveFrom: '', effectiveTo: '', position: 'YUVA EC' });
                onHide();
                if (onSuccess) onSuccess();
            } else {
                // Handle specific error cases
                if (result.statusText === 'YUVA_EC_ALREADY_EXISTS') {
                    toast.error(
                        `YUVA EC Already Assigned!\n\n${result.message}\n\nCurrent YUVA EC: ${result.existingEC.name} (${result.existingEC.position})\nActive until: ${new Date(result.existingEC.effectiveTo).toLocaleDateString()}`,
                        { duration: 8000 }
                    );
                } else if (result.statusText === 'YUVA_EC_DATE_OVERLAP') {
                    toast.error(
                        `Date Conflict!\n\n${result.message}\n\nConflicting YUVA EC: ${result.overlappingEC.name} (${result.overlappingEC.position})\nPeriod: ${new Date(result.overlappingEC.effectiveFrom).toLocaleDateString()} - ${new Date(result.overlappingEC.effectiveTo).toLocaleDateString()}`,
                        { duration: 8000 }
                    );
                } else {
                    toast.error(result.message || 'Failed to assign YUVA EC');
                }
            }
        } catch (error) {
            console.error('Error assigning YUVA EC:', error);
            toast.error('Failed to assign YUVA EC');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemove = async () => {
        if (!student?.userId && !student?._id) {
            toast.error('No student selected');
            return;
        }
        const id = student.userId || student._id;
        if (!window.confirm(`Remove YUVA EC role from ${student.fName || student.name}? This will revoke their EC assignment for this institute.`)) {
            return;
        }
        setIsRemoving(true);
        try {
            const response = await fetch(`${SERVER_ORIGIN}/api/admin/auth/remove-yuva-ec`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': sessionStorage.getItem('token'),
                },
                body: JSON.stringify({ studentId: id }),
            });
            const result = await response.json();
            if (response.ok) {
                toast.success('YUVA EC role removed successfully.');
                setFormData({ effectiveFrom: '', effectiveTo: '', position: '' });
                onHide();
                if (onSuccess) onSuccess();
            } else {
                toast.error(result.message || 'Failed to remove YUVA EC role');
            }
        } catch (error) {
            console.error('Error removing YUVA EC:', error);
            toast.error('Failed to remove YUVA EC role');
        } finally {
            setIsRemoving(false);
        }
    };

    const handleClose = () => {
        setFormData({ effectiveFrom: '', effectiveTo: '', position: '' });
        setExistingEcStatus(null);
        onHide();
    };

    const canRemove = Boolean(student?.isYuvaEC && (student?.userId || student?._id));

    return (
        <Modal show={show} onHide={handleClose} size="md" centered>
            <Modal.Header closeButton>
                <Modal.Title>Assign / Change / Remove YUVA EC</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {student && (
                    <div className="mb-3">
                        <h6>Student Details:</h6>
                        <p className="mb-1"><strong>Name:</strong> {student.fName || student.name}</p>
                        <p className="mb-1"><strong>Email:</strong> {student.email}</p>
                        <p className="mb-1"><strong>Institute:</strong> {student.collegeName || student.institute}</p>
                    </div>
                )}

                {/* Existing YUVA EC Status Warning */}
                {isCheckingStatus && (
                    <Alert variant="info" className="mb-3">
                        <small>Checking existing YUVA EC status...</small>
                    </Alert>
                )}

                {/* {existingEcStatus && existingEcStatus.hasActiveEC && (
                    <Alert variant="warning" className="mb-3">
                        <h6 className="mb-2">⚠️ YUVA EC Already Assigned</h6>
                        <p className="mb-1">
                            <strong>Current YUVA EC:</strong> {existingEcStatus.currentEC.fName} ({existingEcStatus.currentEC.yuvaEcPosition})
                        </p>
                        <p className="mb-1">
                            <strong>Active Period:</strong>{' '}
                            {formatDate(existingEcStatus.currentEC.yuvaEcEffectiveFrom)} -{' '}
                            {formatDate(existingEcStatus.currentEC.yuvaEcEffectiveTo)}
                        </p>

                        <small className="text-muted">
                            Only one YUVA EC can be active per institute. You can assign a new YUVA EC for future dates after the current assignment expires.
                        </small>
                    </Alert>
                )} */}

                {existingEcStatus && existingEcStatus.futureECs && existingEcStatus.futureECs.length > 0 && (
                    <Alert variant="info" className="mb-3">
                        <h6 className="mb-2">📅 Future YUVA EC Assignments</h6>
                        {existingEcStatus.futureECs.map((ec, index) => (
                            // <p key={index} className="mb-1">
                            //     <strong>{ec.fName}</strong> ({ec.yuvaEcPosition}) - {' '}
                            //     {new Date(ec.yuvaEcEffectiveFrom).toLocaleDateString()} to {' '}
                            //     {new Date(ec.yuvaEcEffectiveTo).toLocaleDateString()}
                            // </p>
                            <p key={index} className="mb-1">
                                <strong>{ec.fName}</strong> ({ec.yuvaEcPosition}) -{' '}
                                {formatDate(ec.yuvaEcEffectiveFrom)} to {formatDate(ec.yuvaEcEffectiveTo)}
                            </p>

                        ))}
                        <small className="text-muted">
                            Make sure your new assignment doesn't overlap with existing future assignments.
                        </small>
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Effective From <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="effectiveFrom"
                                    value={formData.effectiveFrom}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Effective To <span className="text-danger">*</span></Form.Label>
                                {/* <Form.Control
                                    type="date"
                                    name="effectiveTo"
                                    value={formData.effectiveTo}
                                    onChange={handleInputChange}
                                    required
                                /> */}
                                <Form.Control
                                    type="date"
                                    name="effectiveTo"
                                    value={formData.effectiveTo}
                                    onChange={handleInputChange}
                                    min={formData.effectiveFrom}
                                    max={
                                        formData.effectiveFrom
                                            ? new Date(new Date(formData.effectiveFrom).setFullYear(
                                                new Date(formData.effectiveFrom).getFullYear() + 1
                                            )).toISOString().split("T")[0]
                                            : undefined
                                    }
                                    required
                                    disabled
                                />

                            </Form.Group>
                        </Col>
                        <small className="text-muted">
                            Duration is fixed to 1 year from the selected start date.
                        </small>

                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Role / Sector <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                            name="position"
                            value={formData.position}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="" disabled>Select role...</option>
                            {roleOptions.map((r) => (
                                <option key={r._id} value={r.name}>
                                    {r.sector ? `${r.name} (${r.sector})` : r.name}
                                </option>
                            ))}
                        </Form.Select>
                        <small className="text-muted">
                            Only one EC can be assigned per role for a particular institute in overlapping dates.
                        </small>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {canRemove && (
                    <Button
                        variant="danger"
                        onClick={handleRemove}
                        disabled={isLoading || isRemoving}
                        className="me-auto"
                    >
                        {isRemoving ? 'Removing...' : 'Remove YUVA EC role'}
                    </Button>
                )}
                <Button variant="secondary" onClick={handleClose} disabled={isLoading || isRemoving}>
                    Cancel
                </Button>
                <Button
                    variant="success"
                    onClick={handleSubmit}
                    disabled={isLoading || isRemoving}
                >
                    {isLoading ? 'Assigning...' : 'Assign / Change YUVA EC'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default YuvaEcAssignmentModal; 